import { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { Gauge } from "@ant-design/plots";
import { toast } from "react-toastify";
import { Link, useNavigate } from "react-router-dom";
import noData from "../../assets/errors/noData.png";
//primereact
import { Card } from "primereact/card";
import { Button } from "primereact/button";
import { Skeleton } from "primereact/skeleton";
import { BreadCrumb } from "primereact/breadcrumb";
//components
import NewEnterprise from "./NewEnterprise";
import Modal from "../../components/modal/Modal";
//services
import { getWhatsapp } from "../../services/WhatsappConnection/getWhatsapp";
import { postWhatsapp } from "../../services/WhatsappConnection/postWhatsapp";
import { useAppContext } from "../../context/AppContext";
import { getEnterprise } from "../../services/GlobalRoles/getEnterprise";
//hooks
import usePermission from "../../hooks/usePermission";
//utils
import { queryClient } from "../../utils/queryClient";

export default function SalesManagement() {
  usePermission("13"); //Visualizar Empreendimentos

  const navigate = useNavigate();
  const [showModal, setShowModal] = useState(false);
  const { permissions } = useAppContext();
  const items = [
    {
      template: () => (
        <Link to={"/salesManagement"} className="p-breadcrumb-link">
          Empreendimentos
        </Link>
      ),
    },
  ];
  const home = {
    template: () => (
      <Link to={"#"} className="p-breadcrumb-link">
        <i className="fa fa-home"></i>
      </Link>
    ),
  };

  const {
    data: enterprises,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ["enterprise"],
    queryFn: () => getEnterprise(navigate),
  });

  const {
    data: whatsappData,
    isLoading: whatsappLoading,
  } = useQuery({
    queryKey: ["whatsapp"],
    queryFn: getWhatsapp,
    enabled: !!permissions?.includes("167"), // Só executa se tiver permissão
  });

  const whatsappMutation = useMutation({
    mutationFn: postWhatsapp,
    onSuccess: () => {
      queryClient.invalidateQueries(["whatsapp"]);
      queryClient.invalidateQueries(["enterprise"]);
    },
    onError: (error) => {
      console.error("Erro ao atualizar WhatsApp:", error);
      toast.error("Erro ao atualizar configuração do WhatsApp");
    },
  });

  const handleWhatsAppToggle = async () => {
    const currentStatus = whatsappData?.whatsapp_all_enterprise ?? false;
    const newStatus = !currentStatus;
    
    try {
      await whatsappMutation.mutateAsync({
        whatsapp_all_enterprise: newStatus
      });
      
      const action = newStatus ? "conectado" : "desconectado";
      toast.success(`WhatsApp ${action} em todos os empreendimentos!`);
    } catch (error) {
      console.error("Erro ao alterar status do WhatsApp:", error);
    }
  };

  const config = {
    range: {
      color: "l(0) 0:#97f1e7 1:#14B8A6",
    },
    startAngle: Math.PI,
    endAngle: 2 * Math.PI,
    indicator: false,
    style: {
      height: "100px",
    },
    statistic: {
      title: {
        offsetY: -32,
        style: {
          fontSize: "32px",
          color: "#4B535E",
        },
      },
      content: {
        style: {
          fontSize: "18px",
          lineHeight: "22px",
          color: "#4B535E",
        },
        formatter: () => "Vendido",
      },
    },
  };

  return (
    <>
      <Modal
        visible={showModal}
        position="top"
        onHide={() => setShowModal(false)}
        dynamicTitle="Novo Empreendimento"
      >
        <NewEnterprise setShowModal={setShowModal} />
      </Modal>

      <div className="row d-flex justify-content-between mb-3">
        <div className="col-md-4">
          <BreadCrumb model={items} home={home} className="breadCrumb" />
        </div>
        {permissions?.includes("14") && ( //Criar Empreendimentos
          <div className="col-md-8">
            <div className="float-end">
              <div className="col-md-12 justify-content-center d-flex gap-2">
                {permissions?.includes("167") && ( //Modulo WhatsApp
                  <Button
                    rounded
                    icon="fa-brands fa-whatsapp"
                    severity={whatsappData?.whatsapp_all_enterprise ? "success" : "danger"}
                    onClick={handleWhatsAppToggle}
                    loading={whatsappMutation.isLoading}
                    disabled={whatsappLoading || whatsappMutation.isLoading}
                    tooltip={`${whatsappData?.whatsapp_all_enterprise ? "Desconectar" : "Conectar"} WhatsApp em todos os empreendimentos`}
                    tooltipOptions={{ position: "top" }}
                    className="p-inputtext-sm"
                  />
                )}
                <Button
                  rounded
                  label="Novo Empreendimento"
                  icon="fa fa-user-plus"
                  onClick={() => setShowModal(true)}
                  className="p-inputtext-sm"
                />
              </div>
            </div>
          </div>
        )}
      </div>
      <div className="row">
        {isLoading && (
          <>
            {Array.from({ length: 4 }).map((_, index) => (
              <div key={index} className="col-xl-3 col-md-6 pb-4">
                <div
                  className="card card-sales-management flex justify-content-center text-center rounded-4 px-4 hover-card"
                  style={{ height: "100%" }}
                >
                  <Card className="border-none shadow-none">
                    <div className="row d-flex justify-content-center">
                      <div className="pb-3">
                        <Skeleton
                          width="75%"
                          height="2rem"
                          className="mx-auto"
                        ></Skeleton>
                      </div>
                      <div className="pb-5">
                        <Skeleton
                          width="75%"
                          height="8rem"
                          className="mx-auto"
                        ></Skeleton>
                      </div>
                      <div className="col-12">
                        <Skeleton
                          width="100%"
                          height="6rem"
                          className="mx-auto"
                        ></Skeleton>
                      </div>
                      <div className="col-3 my-2 me-2 pt-5">
                        <Skeleton width="50px" height="50px"></Skeleton>
                      </div>
                      <div className="col-3 my-2 me-2 pt-5">
                        <Skeleton width="50px" height="50px"></Skeleton>
                      </div>
                      <div className="col-3 my-2 me-2 pt-5">
                        <Skeleton width="50px" height="50px"></Skeleton>
                      </div>
                    </div>
                  </Card>
                </div>
              </div>
            ))}
          </>
        )}
        {!enterprises && isError && (
          <div className="d-flex flex-column justify-content-center align-items-center text-center">
            <img src={noData} alt="error" width="400px" />
            <h4>Ocorreu um erro ao buscar os empreendimentos.</h4>
          </div>
        )}
        {!isError && enterprises?.length === 0 ? (
          <div className="d-flex flex-column justify-content-center align-items-center text-center">
            <img src={noData} alt="error" width="400px" />
            <h4>Não encontramos nenhum empreendimento por aqui</h4>
          </div>
        ) : (
          enterprises?.map((enterprise, index) => (
            <div key={index} className="col-xl-3 col-md-6 pb-4">
              <div
                className="card card-sales-management flex justify-content-center text-center rounded-4 px-4 hover-card"
                style={{ height: "100%" }}
              >
                <Card className="border-none shadow-none">
                  <div className="row d-flex justify-content-center">
                    <div style={{ minHeight: 236 }}>
                      {enterprise.files[0] && enterprise.file !== null ? (
                        <>
                          <div className="row text-center w-auto">
                            <h4 className="text-secondary my-2">
                              {enterprise.title}
                            </h4>
                          </div>
                          <img
                            src={
                              enterprise.files[0]
                                ? enterprise.files[0].path
                                : ""
                            }
                            className="rounded w-100"
                            alt="logo empreendimento"
                          />
                        </>
                      ) : (
                        <h2 className="my-4">{enterprise.title}</h2>
                      )}
                    </div>
                    <div className="pt-2">
                      <Gauge
                        {...config}
                        percent={
                          enterprise.lands_stauts_count[0] &&
                          enterprise.lands_stauts_count[0].sold_percentage
                            ? Math.floor(
                                enterprise.lands_stauts_count[0].sold_percentage
                                  ? enterprise.lands_stauts_count[0]
                                      .sold_percentage
                                  : 0
                              ) / 100
                            : 0
                        }
                        statistic={{
                          title: {
                            offsetY: -32,
                            style: {
                              fontSize: "32px",
                              color: "#4B535E",
                            },
                            formatter: () =>
                              `${Math.floor(
                                enterprise.lands_stauts_count[0] &&
                                  enterprise.lands_stauts_count[0]
                                    .sold_percentage
                                  ? enterprise.lands_stauts_count[0]
                                      .sold_percentage
                                  : 0
                              )}%`,
                          },
                          content: {
                            style: {
                              fontSize: "18px",
                              lineHeight: "22px",
                              color: "#4B535E",
                            },
                            formatter: () => "Vendido",
                          },
                        }}
                      />
                    </div>

                    <div className="pt-5">
                      {permissions?.includes("167") && ( //Modulo WhatsApp
                        <Button
                          rounded
                          className="my-2 me-2"
                          icon="fa-brands fa-whatsapp"
                          severity={enterprise.whatsapp_status === 1 ? "success" : "danger"}
                          onClick={() =>
                            permissions?.includes("167")
                              ? navigate(`/whatsappConfig/${enterprise.id}`, {
                                  state: { name: enterprise.title },
                                })
                              : toast.warning("Contrate o Módulo de WhatsApp")
                          }
                          tooltip="WhatsApp"
                          tooltipOptions={{ position: "top" }}
                        />
                      )}
                      {permissions?.includes("15") && ( // Visualizar Gestao de Vendas
                        <Button
                          rounded
                          icon="fa-solid fa-hand-holding-dollar"
                          className="my-2 me-2"
                          onClick={() =>
                            navigate(`/enterprise/${enterprise.id}`, {
                              state: { name: enterprise.title },
                            })
                          }
                          tooltip="Gestão de Vendas"
                          tooltipOptions={{ position: "top" }}
                        />
                      )}
                      {permissions?.includes("16") && ( // Visualizar Configuracoes do Empreendimento
                        <Button
                          rounded
                          // outlined
                          className="my-2 me-2"
                          icon="fa-solid fa-gears"
                          severity="secondary"
                          onClick={() =>
                            navigate(`/enterpriseSettings/${enterprise.id}`, {
                              state: { name: enterprise.title },
                            })
                          }
                          tooltip="Configurações"
                          tooltipOptions={{ position: "top" }}
                        />
                      )}
                    </div>
                  </div>
                  {enterprise.lands_stauts_count &&
                    enterprise.lands_stauts_count.length > 0 && (
                      <div
                        style={{
                          position: "absolute",
                          width: "100%",
                          bottom: 0,
                          left: 0,
                        }}
                      ></div>
                    )}
                </Card>
              </div>
            </div>
          ))
        )}
      </div>
    </>
  );
}
